import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Cpu, HardDrive, Zap, ShieldCheck, Server, Network, Shield, CheckCircle, BarChart3, HelpCircle, Mail, MessageCircle, Clock, Award, X as XIcon, BookOpen, ArrowRight } from 'lucide-react';
import { API_URLS } from '../../config/api';
import MediaDisplay from '../MediaDisplay';
import { formatPrice, formatNumber } from '../../utils/format';

const normalize = (s) => (s || '')
  .toString()
  .trim()
  .toLowerCase()
  .replace(/ı/g,'i')
  .replace(/i̇/g,'i')
  .replace(/ş/g,'s')
  .replace(/ğ/g,'g')
  .replace(/ç/g,'c')
  .replace(/ö/g,'o')
  .replace(/ü/g,'u')
  .replace(/\s+/g, ' ');
const matchesCorporate = (val) => {
  const n = normalize(val);
  return n === 'kurumsal sunucu' || 
         n === 'kurumsal server' ||
         n === 'kurumsal-sunucu' ||
         n === 'kurumsal-server' ||
         (n.includes('kurumsal') && (n.includes('sunucu') || n.includes('server')));
};

const CorporateServerPage = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [blogs, setBlogs] = useState([]);
  const [blogsLoading, setBlogsLoading] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [whatsappNumber, setWhatsappNumber] = useState('905324404011');
  const [contentReady, setContentReady] = useState(false);
  const [pageContent, setPageContent] = useState(null);
  const toBool = (v) => v === true || v === '1' || v === 1;

  useEffect(() => {
    // Fade-in animasyonu için
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);
    const fetchPageContent = async () => {
      try {
        const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getPageContent&slug=kurumsal-sunucu`);
        const txt = await res.text();
        let json; try { json = JSON.parse(txt); } catch { json = {}; }
        if (json?.status === 'success' && json?.data) {
          setPageContent(json.data);
        }
      } catch (e) {
        // keep fallbacks
        console.error('Corporate page content fetch error:', e);
      }
    };
    const fetchPackages = async () => {
      setLoading(true);
      try {
        const res = await fetch(API_URLS.CORPORATE_SERVER_PACKAGES);
        const text = await res.text();
        let data; try { data = JSON.parse(text); } catch { data = []; }
        const rawList = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        
        // Parse features - obje yapısını koru
        const list = rawList.map(pkg => {
          let rawFeatures = Array.isArray(pkg.features) ? pkg.features : [];
          const parsedFeatures = rawFeatures.map(f => {
            if (typeof f === 'string') {
              return { name: f, text: f, included: true };
            }
            if (f && typeof f === 'object') {
              return {
                name: f.name || f.text || '',
                text: f.text || f.name || '',
                included: f.included !== false
              };
            }
            return { name: String(f), text: String(f), included: true };
          });
          return { ...pkg, features: parsedFeatures };
        });
        
        setPackages(list);
      } catch (e) {
        console.error('Corporate packages fetch error:', e);
        setPackages([]);
      } finally {
        setLoading(false);
      }
    };
    const fetchBlogs = async () => {
      setBlogsLoading(true);
      try {
        // Try server-side filtered fetch first with different category slug variations
        const categorySlugs = ['kurumsal-sunucu', 'kurumsal-server'];
        let list = [];
        
        for (const slug of categorySlugs) {
          const res = await fetch(`${API_URLS.BLOGS}&category=${slug}&limit=3`);
          const txt = await res.text();
          let data; 
          try { 
            data = JSON.parse(txt); 
          } catch { 
            data = { status: 'error', blogs: [] }; 
          }
          
          // Handle different response formats (same as BlogPage.jsx)
          list = Array.isArray(data) ? data : (data.blogs || data.data || []);
          
          if (list && list.length > 0) {
            break;
          }
        }
        
        // Fallback: fetch all then client-side filter if empty
        if (!list || list.length === 0) {
          const allRes = await fetch(API_URLS.BLOGS);
          const allTxt = await allRes.text();
          let allData; 
          try { 
            allData = JSON.parse(allTxt); 
          } catch { 
            allData = { status: 'error', blogs: [] }; 
          }
          
          // Handle different response formats (same as BlogPage.jsx)
          const allList = Array.isArray(allData) ? allData : (allData.blogs || allData.data || []);
          
          // Get all unique categories
          const uniqueCategories = [...new Set(allList.map(b => b.category || b.category_name || 'none'))];
          
          list = allList.filter(b => {
            // Check category field (slug)
            const categorySlug = (b.category || '').toString().toLowerCase().trim();
            // Check category_name field
            const categoryName = (b.category_name || '').toString().toLowerCase().trim();
            
            const matches = categorySlug === 'kurumsal-sunucu' || 
                   categorySlug === 'kurumsal-server' ||
                   matchesCorporate(categorySlug) ||
                   matchesCorporate(categoryName);
            
            return matches;
          }).slice(0, 3);
        }
        
        const filtered = list
          .filter(b => b.is_published === 1 || b.is_published === true || b.is_published === '1' || b.is_published === undefined)
          .sort((a,b) => {
            const dateA = new Date(a.published_at || a.created_at || a.createdAt || 0);
            const dateB = new Date(b.published_at || b.created_at || b.createdAt || 0);
            return dateB - dateA || (b.id||0) - (a.id||0);
          })
          .slice(0, 3)
          .map(b => ({
            id: b.id || b.slug || b.title,
            title: b.title || b.name || 'Blog',
            excerpt: b.excerpt || b.summary || '',
            href: b.slug ? `/blog/${b.slug}` : `/blog/${b.id || ''}`,
            image: b.image || b.thumbnail || b.cover_image || '',
            tag: b.category_name || b.category || 'Kurumsal Sunucu'
          }));
        setBlogs(filtered);
      } catch (e) {
        console.error('Error fetching corporate server blogs:', e);
        setBlogs([]);
      } finally {
        setBlogsLoading(false);
      }
    };
    const fetchFaqs = async () => {
      try {
        const res = await fetch(API_URLS.FAQ);
        const text = await res.text();
        let data; try { data = JSON.parse(text); } catch { data = []; }

        if (Array.isArray(data?.categories) && Array.isArray(data?.questions)) {
          const cat = data.categories.find(c => matchesCorporate(c?.title));
          const filtered = data.questions
            .filter(q => q.is_active == 1 && (cat ? q.category_id === cat.id : false))
            .sort((a,b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
            .map(q => ({ question: q.question || 'Soru', answer: q.answer || '' }));
          setFaqs(filtered);
          return;
        }

        const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        const flat = list.filter(item => {
          const candidates = [];
          if (item.categories) candidates.push(...(Array.isArray(item.categories) ? item.categories : [item.categories]));
          if (item.category) candidates.push(...(Array.isArray(item.category) ? item.category : [item.category]));
          if (item.category_title) candidates.push(item.category_title);
          if (item.categoryTitle) candidates.push(item.categoryTitle);
          if (item.category_name) candidates.push(item.category_name);
          return candidates.some(c => matchesCorporate(c?.title || c));
        }).map(f => ({ question: f.question || f.title || 'Soru', answer: f.answer || f.content || '' }));
        setFaqs(flat);
      } catch (e) {
        setFaqs([]);
      }
    };
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getCompanySettings`);
        if (res.ok) {
          const json = await res.json();
          if (json.status === 'success' && json.data?.whatsapp) {
            setWhatsappNumber(json.data.whatsapp);
          }
        }
      } catch (e) {
        console.error('WhatsApp numarası alınamadı:', e);
      }
    };
    fetchPageContent();
    fetchPackages();
    fetchBlogs();
    fetchFaqs();
    fetchSettings();
    
    return () => clearTimeout(timer);
  }, []);

  const additional = useMemo(() => pageContent?.additional_sections || {}, [pageContent]);
  const heroImage = additional?.hero?.image_url;
  const heroBadge = pageContent?.hero_badge_text || 'Kurumsal Sunucu';
  const heroTitle = pageContent?.hero_title || 'Kurumsal Sunucu Kiralama';
  const heroDesc = pageContent?.hero_description || 'Yüksek kaynak ihtiyaçlarına özel, güçlü donanımlar ve tam kontrol ile kurumsal performansı yakalayın.';
  const heroHighlight = additional?.hero?.highlight_text || 'Tam Kontrol, Kurumsal Performans';

  const introSection = additional?.intro_section || {};
  const featuresSection = additional?.features_section || {};
  const featureCards = Array.isArray(featuresSection.cards) ? featuresSection.cards : [];
  const standardSection = additional?.standard_section || {};
  const standardCards = Array.isArray(standardSection.cards) ? standardSection.cards : [];
  const valueProductsSection = additional?.value_products_section || {};
  const valueProductCards = Array.isArray(valueProductsSection.cards) ? valueProductsSection.cards : [];

  // Dedicated paket kart stilini yansıtan kart
  const CSCard = ({ plan, index }) => {
    const isPopular = toBool(plan.popular ?? plan.is_popular);
    return (
      <Card className={`relative overflow-visible border-0 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 cursor-pointer group ${isPopular ? 'ring-4 ring-pink-200' : ''} ${plan.bgColor || 'bg-orange-50'}`} style={{animationDelay: `${index * 0.1}s`,animation: 'fadeInUp 0.6s ease-out forwards'}}>
        {isPopular && (
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
          <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 text-sm font-bold animate-pulse flex items-center justify-center" style={{ width: '150px' }}>
            POPÜLER PAKET
          </Badge>
        </div>
      )}
      <CardHeader className="space-y-4 pb-6">
        <div className="space-y-4">
          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${plan.color || 'from-orange-600 to-red-600'} flex items-center justify-center transition-all duration-500`}>
            <Server className="w-8 h-8 text-white" />
          </div>
          <div className="space-y-2">
            <CardTitle className="text-2xl font-bold text-gray-900">{plan.name}</CardTitle>
            <p className="text-gray-600 leading-relaxed">Yüksek performanslı Kurumsal sunucu çözümü</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center space-x-3"><Cpu className="w-5 h-5 text-orange-600" /><div><p className="font-semibold text-gray-900">{plan.cpu?.cores || plan.cpuCores || ''} Çekirdek</p><p className="text-xs text-gray-600">{plan.cpu?.model || plan.cpuModel || 'Intel® Xeon'}</p></div></div>
          <div className="flex items-center space-x-3"><BarChart3 className="w-5 h-5 text-emerald-600" /><div><p className="font-semibold text-gray-900">{plan.ram}</p><p className="text-xs text-gray-600">DDR4 RAM</p></div></div>
          <div className="flex items-center space-x-3"><HardDrive className="w-5 h-5 text-purple-600" /><div><p className="font-semibold text-gray-900">{plan.storage}</p><p className="text-xs text-gray-600">Enterprise SSD</p></div></div>
          <div className="flex items-center space-x-3"><Network className="w-5 h-5 text-blue-600" /><div><p className="font-semibold text-gray-900">{plan.bandwidth}</p><p className="text-xs text-gray-600">Bant Genişliği</p></div></div>
        </div>
        <div className="space-y-2">
          {plan.price ? (
            <>
              <div className="flex items-baseline space-x-2"><span className="text-4xl font-bold text-gray-900">{formatPrice(plan.price, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span><span className="text-gray-600">/yıllık</span></div>
              {(plan.originalPrice || plan.discount) ? (
                <div className="flex items-center space-x-2">
                  {plan.originalPrice ? (<span className="text-lg text-gray-400 line-through">{formatPrice(plan.originalPrice, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>) : null}
                  {plan.discount ? (<Badge variant="destructive" className="animate-bounce">%{plan.discount} İndirim</Badge>) : null}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {Array.isArray(plan.features) && plan.features.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-800">Özellikler:</h4>
            <div className="grid grid-cols-2 gap-2">
              {plan.features.map((feature, idx) => {
                const isIncluded = feature.included !== false;
                // Güvenli string extraction
                const featureText = typeof feature === 'string'
                  ? feature
                  : (feature?.text || feature?.name || String(feature));
                return (
                  <div key={idx} className="flex items-center space-x-2">
                    {isIncluded ? (
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <XIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}
                    <span className={`text-xs font-medium ${isIncluded ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                      {featureText}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <Button className="w-full h-12 font-semibold bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white">
          <span>Yapılandır</span>
        </Button>
      </CardContent>
    </Card>
    );
  };

  const fallbackPlans = [
    { id: 'cs-1', name: 'Xtreme 10 Core', cpu: { cores: 10, model: 'Intel® Xeon Silver 4210R' }, ram: '32 GB', storage: '2 x 480 GB SSD', bandwidth: '1 Gbit', price: 3399, originalPrice: 3899, discount: 13, color: 'from-orange-600 to-red-600', bgColor: 'bg-orange-50', popular: false, features: ['IP/KVM', 'DDoS Koruması', '%99,9 Uptime', 'Türkiye Lokasyon'] },
    { id: 'cs-2', name: 'Xtreme 12 Core', cpu: { cores: 12, model: 'Intel® Xeon Silver 4214R' }, ram: '32 GB', storage: '2 x 480 GB SSD', bandwidth: '1 Gbit', price: 3799, originalPrice: 4399, discount: 14, color: 'from-purple-600 to-pink-600', bgColor: 'bg-purple-50', popular: true, features: ['IP/KVM', 'DDoS Koruması', '%99,9 Uptime', 'Yedekli Operatör'] },
    { id: 'cs-3', name: 'Xtreme 16 Core', cpu: { cores: 16, model: 'Intel® Xeon Silver 4314' }, ram: '32 GB', storage: '2 x 480 GB SSD', bandwidth: '1 Gbit', price: 4299, originalPrice: 4999, discount: 14, color: 'from-green-600 to-emerald-600', bgColor: 'bg-green-50', popular: false, features: ['IP/KVM', 'DDoS Koruması', '%99,9 Uptime', 'RAID'] }
  ];

  const mapToPlans = (pkgs) => {
    if (!Array.isArray(pkgs) || pkgs.length === 0) return fallbackPlans;
    return pkgs.map((p, i) => ({
      id: p.id || `cs-${i+1}`,
      name: p.title || p.name || `Kurumsal-${i+1}`,
      cpu: { cores: Number(p.cpu_cores) || undefined, model: p.cpu_model || undefined },
      cpuCores: Number(p.cpu_cores) || undefined,
      cpuModel: p.cpu_model || undefined,
      ram: p.ram || '32 GB',
      storage: p.storage || p.disk || '2 x 480 GB SSD',
      bandwidth: p.bandwidth || p.network || '1 Gbit',
      price: p.price ? Number(p.price) : undefined,
      originalPrice: p.original_price ? Number(p.original_price) : undefined,
      discount: p.discount ? Number(p.discount) : undefined,
      color: p.color || undefined,
      bgColor: p.bg_color || undefined,
      popular: (p.is_popular === '1' || p.is_popular === 1 || p.is_popular === true),
      features: Array.isArray(p.features) ? p.features : ['IP/KVM', 'DDoS Koruması']
    }));
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero (En Üstte) */}
      <section 
        className="relative overflow-hidden"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-orange-600" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl md:text-5xl font-extrabold text-white">{heroTitle}</h1>
              <p className="mt-4 text-white/90 text-lg">{heroDesc}</p>
            </div>
            <div className="hidden lg:block">
              <div className="rounded-2xl bg-white/10 border border-white/10 overflow-hidden">
                <MediaDisplay
                  src={heroImage || "https://via.placeholder.com/600x400/F97316/FFFFFF?text=Kurumsal+Sunucu"}
                  alt="Kurumsal Sunucu"
                  className="w-full h-full object-cover"
                  videoProps={{ controls: true }}
                  imgProps={{ loading: "lazy" }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Paketler */}
      <section 
        className="py-16 bg-gray-50"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Kurumsal Sunucu Paketleri</h2>
            <p className="text-gray-600">İhtiyacınıza en uygun donanımı seçin</p>
          </div>
          {loading && (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600"></div>
            </div>
          )}
          {!loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {mapToPlans(packages).map((plan, idx) => (
                <CSCard key={plan.id || idx} plan={plan} index={idx} />
              ))}
            </div>
          )}
        </div>
      </section>

      

      {/* Kurumsal Sunucu Nedir? */}
      <section 
        className="py-16 bg-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center flex-col-reverse lg:grid">
            <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg overflow-hidden">
              <MediaDisplay
                src={introSection.image_url || "https://via.placeholder.com/600x400/F97316/FFFFFF?text=Kurumsal+Sunucu"}
                alt="Kurumsal Sunucu Nedir?"
                className="w-full h-full object-cover"
                videoProps={{ controls: true }}
                imgProps={{ loading: "lazy" }}
              />
            </div>
            <div className="text-center lg:text-left">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{introSection.title || 'Kurumsal Sunucu Nedir?'}</h2>
              <p className="text-gray-700 text-lg">{introSection.description || ''}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tek Sunucuda Üstün Performans */}
      <section 
        className="py-16 bg-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">{featuresSection.title || 'Tek Sunucuda Üstün Performans'}</h2>
            <p className="text-gray-600">{featuresSection.subtitle || 'Sunucu performansınızı zirveye taşıyacak üstün özellikler'}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(featureCards.length ? featureCards : []).map((f, i) => {
              const title = f.title || '';
              const desc = f.description || f.desc || '';
              const Icon = (f.icon === 'Cpu' ? Cpu :
                            f.icon === 'HardDrive' ? HardDrive :
                            f.icon === 'Server' ? Server :
                            f.icon === 'ShieldCheck' ? ShieldCheck :
                            f.icon === 'Zap' ? Zap :
                            BarChart3);
              return (
              <Card key={i} className="shadow-sm">
                <CardHeader className="flex flex-row items-center gap-3">
                  <Icon className="w-6 h-6 text-orange-600" />
                  <CardTitle className="text-gray-900 text-lg">{title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{desc}</p>
                </CardContent>
              </Card>
            );})}
          </div>
        </div>
      </section>

      {/* Ortak Özellikler */}
      <section 
        className="py-16 bg-orange-50"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">{standardSection.title || 'Ortak Özellikler'}</h2>
            <p className="text-gray-600">{standardSection.subtitle || 'Tüm Kurumsal Sunucu paketlerinde standart'}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {(standardCards.length ? standardCards : []).map((it,i)=>{
              const text = it.title || it.text || '';
              const Icon = (it.icon === 'Network' ? Network :
                            it.icon === 'ShieldCheck' ? ShieldCheck :
                            it.icon === 'Shield' ? Shield :
                            it.icon === 'HardDrive' ? HardDrive :
                            Cpu);
              return (
              <div key={i} className="rounded-lg bg-white border border-orange-100 p-6 text-gray-900 font-medium flex flex-col items-center gap-3">
                <Icon className="w-7 h-7 text-orange-600" />
                <div>{text}</div>
              </div>
            );})}
          </div>
        </div>
      </section>

      {/* Üstün Teknoloji, Tam Sunucu Kontrolü */}
      <section 
        className="py-16 bg-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Üstün Teknoloji, Tam Sunucu Kontrolü</h2>
            <p className="text-gray-600">Gelişmiş Sunucu ve Network Altyapısı</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { icon: Network, title:'Yedekli Network Altyapısı',desc:'Yedekli network erişimi sayesinde kesintiler işinizi etkilemez.'},
              { icon: Zap, title:'Ücretsiz İlk Kurulum',desc:'Siparişinizden kısa süre sonra ücretsiz kurulum yapılır.'},
              { icon: Server, title:'Tam Sunucu Kontrolü',desc:'Panel ya da API ile yeniden başlatma, IP/KVM, yedekleme ve yeniden kurulum.'},
              { icon: ShieldCheck, title:'Garantili Donanımlar',desc:'Donanım arızalarında garantili donanım ücretsiz ve hızlı şekilde sağlanır.'},
              { icon: Cpu, title:'Genişletilebilir Kaynaklar',desc:'İhtiyaçlarınıza göre donanım/yazılım kaynakları artırılabilir.'},
              { icon: ShieldCheck, title:'%99,9 Uptime Garantisi',desc:'Standart SLA kapsamında network ve donanım uptime garantisi.'}
            ].map((it, i) => (
              <Card key={i} className="shadow-sm">
                <CardHeader className="flex flex-row items-center gap-3">
                  <it.icon className="w-6 h-6 text-orange-600" />
                  <CardTitle className="text-gray-900">{it.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{it.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Sunucu ile İlgili Güncel İçerikler */}
      <section 
        className="py-16 bg-gray-100"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Sunucu ile İlgili Güncel İçerikler</h2>
            <p className="text-gray-600">Sunucu, yapılandırma ve kontrol panelleri ile ilgili içerikler</p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {(blogs.length ? blogs : []).map((b) => {
              const hasValidImage = b.image && 
                b.image !== 'https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?auto=format&fit=crop&w=800&q=80' &&
                !b.image.includes('placeholder');
              
              return (
                <Card key={b.id} className="overflow-hidden border-orange-100 hover:shadow-lg transition-shadow">
                  {hasValidImage ? (
                    <div className="aspect-[16/9] bg-orange-50 overflow-hidden">
                      <MediaDisplay
                        src={b.image}
                        alt={b.title}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                        videoProps={{ controls: true }}
                        imgProps={{
                          loading: "lazy",
                          onError: (e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div class="w-full h-full bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center"><svg class="w-16 h-16 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <div className="aspect-[16/9] bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center">
                      <svg className="w-16 h-16 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                    </div>
                  )}
                  <CardContent className="p-5 space-y-3">
                    <div className="inline-flex items-center px-3 py-1 bg-orange-50 text-orange-600 text-xs font-semibold rounded-full">
                      <BookOpen className="w-3.5 h-3.5 mr-1" />
                      {b.tag}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{b.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{b.excerpt || ''}</p>
                    <button
                      onClick={() => window.open(b.href, '_self')}
                      className="inline-flex items-center text-sm font-semibold text-orange-600 hover:text-orange-700"
                    >
                      Yazıyı Oku
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                  </CardContent>
                </Card>
              );
            })}
            {!blogsLoading && blogs.length === 0 && (
              <div className="col-span-3 text-center text-gray-500">Bu kategori için içerik bulunamadı.</div>
            )}
            {blogsLoading && (
              <div className="col-span-3 flex justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600"></div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* İşinize Değer Katacak Ürünler */}
      <section 
        className="py-16 bg-gradient-to-r from-orange-600 to-orange-700"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 text-white">
            <h2 className="text-3xl md:text-4xl font-bold">{valueProductsSection.title || 'İşinize Değer Katacak Ürünler'}</h2>
            <p className="text-white/90">{valueProductsSection.subtitle || 'Kurumsal sunucunuzla birlikte tercih edebileceğiniz ürünler'}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(valueProductCards.length ? valueProductCards : []).map((x,i)=> {
              const title = x.title || `Ürün ${i+1}`;
              const href = x.link || x.href || '#';
              return (
                <a key={i} href={href} className="block bg-white rounded-lg shadow hover:shadow-lg overflow-hidden p-6">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="w-6 h-6 text-orange-600" />
                    <div className="text-lg font-semibold text-gray-900">{title}</div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sıkça Sorulan Sorular */}
      <section 
        className="py-16 bg-gray-50"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Sıkça Sorulan <span className="text-orange-600">Sorular</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Kurumsal Sunucu ile ilgili merak ettiğiniz soruların cevaplarını burada bulabilirsiniz
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {(faqs.length ? faqs : [
                { question: 'Kurumsal Sunucu nedir?', answer: 'Yüksek kaynak ihtiyaçlarına yönelik, tam kontrol sahibi olabileceğiniz fiziksel sunucu hizmetidir.' },
                { question: 'Donanım yükseltmesi yapılabilir mi?', answer: 'Sunucunun desteklediği sınırlar dahilinde yükseltme yapılabilir.' }
              ]).map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`}>
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center">
                      <HelpCircle className="w-5 h-5 mr-3 text-orange-600" />
                      {f.question}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Corporate Server Contact Section */}
      <section 
        className="py-20 bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 text-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl font-bold mb-4">Kurumsal Sunucu İhtiyacınız İçin Bize Ulaşın</h2>
            <p className="text-xl text-orange-100 mb-8 max-w-3xl mx-auto">
              Yüksek performanslı kurumsal sunucu çözümlerimiz hakkında detaylı bilgi almak ve size özel paket önerileri için ekibimizle iletişime geçin.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="/iletisim"
                className="inline-flex items-center px-8 py-4 bg-white text-orange-600 rounded-lg hover:bg-gray-100 font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                <Mail className="w-5 h-5 mr-2" />
                İletişime Geç
              </a>
              <a
                href={`https://api.whatsapp.com/send/?phone=${encodeURIComponent(whatsappNumber)}&text=${encodeURIComponent('Merhaba, kurumsal sunucu hizmetleriniz hakkında bilgi almak istiyorum.')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold text-lg transition-all duration-300 transform hover:scale-105 border-2 border-white shadow-lg"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp İletişim
              </a>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <Clock className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">7/24 Destek</h3>
                <p className="text-orange-100">Kurumsal sunucu konusunda kesintisiz destek</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <Shield className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">Güvenli Altyapı</h3>
                <p className="text-orange-100">Tam kontrollü fiziksel sunucular</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <Award className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">Uzman Ekip</h3>
                <p className="text-orange-100">Kurumsal sunucu konusunda deneyimli ekibimiz</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CorporateServerPage;


